import {
  isOutsideOperationalHoursOn,
  normalizeTime,
  timeSlots,
  windowForDate,
} from "@1-now/booking-core";

type Hours = Parameters<typeof windowForDate>[0];

/**
 * The slots the picker offers on `date`, inside the hours `location` keeps that day. Null when the
 * location is closed all day; [] when it states no hours or no slot falls inside them.
 */
const openSlots = (location: Hours, date: Date | null) => {
  const window = windowForDate(location, date);
  if (window === null) return null;
  if (!window.start || !window.end) return { window, open: [] as string[] };
  return { window, open: timeSlots.filter((slot) => !isOutsideOperationalHoursOn(slot, location, date)) };
};

/**
 * The pick-up time a search gets, inside the hours the pick-up location keeps on the pick-up date
 * (1NOW-11108). One rule:
 *
 * - An open slot stays as it is.
 * - Outside the window, before opening or after closing: the FIRST open slot of that day.
 * - Inside the window but off the picker's half-hour grid: the next open slot, or the last one
 *   when none is left. QA's "loc" opens 02:45 to 04:15; 02:45 is inside it but is not a slot, so a
 *   renter could keep it, and only 03:00, 03:30 and 04:00 can be chosen.
 * - No hours kept, or no slot inside them: `time` as it was.
 * - Closed that day: null. The closed-day handling owns that case, so callers leave the time.
 */
export const snapToOpenSlot = (time: string, location: Hours, date: Date | null): string | null => {
  const slots = openSlots(location, date);
  if (slots === null) return null;
  const wanted = normalizeTime(time);
  const { window, open } = slots;
  if (open.length === 0 || open.includes(wanted)) return wanted;
  if (isOutsideOperationalHoursOn(wanted, location, date)) {
    // "00:00" can be open on a day that follows a midnight close; the day's first slot is the
    // opening one.
    return open.find((slot) => slot >= normalizeTime(window.start!)) ?? open[0];
  }
  return open.find((slot) => slot > wanted) ?? open[open.length - 1];
};

/**
 * The nearest open slot: the first at or after `time`, else the last one. What the return time
 * uses at a drop-off location that keeps other hours than the pick-up location.
 */
const nearestOpenSlot = (time: string, location: Hours, date: Date | null): string | null => {
  const slots = openSlots(location, date);
  if (slots === null) return null;
  const wanted = normalizeTime(time);
  const { open } = slots;
  if (open.length === 0) return wanted;
  return open.includes(wanted) ? wanted : open.find((slot) => slot > wanted) ?? open[open.length - 1];
};

/**
 * The return time a trip gets until the renter picks one themselves (1NOW-11108): the pick-up
 * clock time, so a 3-night trip is 3 days and not 4.
 *
 * - No hours kept for the drop-off location: the pick-up time exactly.
 * - Open that day and the pick-up time is an open slot: the pick-up time.
 * - Open that day, pick-up time outside it: the nearest slot, which is the last one when the
 *   pick-up is after closing and the first one when before opening.
 * - Closed that day: null. The closed-day handling owns that case, so the caller leaves the
 *   return time as it is.
 */
export const pickReturnTime = (
  pickupTime: string,
  dropLocation: Hours,
  returnDate: Date | null,
): string | null => nearestOpenSlot(pickupTime, dropLocation, returnDate);
